import { useEffect, useState } from "react";
import { Button, Modal, Progress, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ApiService from "../../core/service/api-service";
import { dateFormat } from "../../core/utils/date-utils";
import ContractEditModal from "./ContractEditModal";
import { CONTRACT_STATUS_COLOR, PATIENT_CLASSES, contractStatus, formatNumber } from "./contract-utils";

/**
 * Sözleşme listesi. Sayı (usedCount) sunucuda günlük hesaplanıp saklanır; burada yalnız okunur.
 * "Şimdi hesapla" tek sözleşmeyi anında yeniden sayar.
 *
 * Sayfalama yok: sözleşme sayısı hastane başına birkaç tane.
 */
export default function ContractGrid() {
    const { t } = useTranslation();

    const [options, setOptions] = useState({ hospitals: [], modalities: [] })
    const [contracts, setContracts] = useState([])
    const [hospitalId, setHospitalId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [recalculatingId, setRecalculatingId] = useState(null)
    // null → kapalı, {} → yeni sözleşme, satır → düzenleme
    const [editing, setEditing] = useState(null)

    const loadOptions = async () => {
        const res = await ApiService.HospitalContract.GetOptions();
        if (res.success)
            setOptions(res.data)
    }

    const load = async () => {
        setLoading(true)
        try {
            const res = await ApiService.HospitalContract.GetList(hospitalId);
            if (res.success)
                setContracts(res.data || [])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadOptions()
    }, []);

    useEffect(() => {
        load()
    }, [hospitalId]);

    const replaceRow = (row) => setContracts(list => list.map(x => x.id === row.id ? row : x))

    const recalculate = async (row) => {
        setRecalculatingId(row.id)
        try {
            const res = await ApiService.HospitalContract.Recalculate(row.id);
            if (res.success)
                replaceRow(res.data)
        } finally {
            setRecalculatingId(null)
        }
    }

    const remove = (row) => {
        Modal.confirm({
            title: t('Delete Contract'),
            content: row.name,
            okText: t('Delete'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => {
                const res = await ApiService.HospitalContract.Delete(row.id);
                if (res.success)
                    await load()
            }
        });
    }

    // Saat dilimi kaymasın diye tarih yalnız gün olarak (yyyy-MM-dd) yorumlanır.
    const today = moment().format('YYYY-MM-DD');

    const modalityName = (id) => options.modalities.find(x => x.id === id)?.name ?? id;

    const columns = [
        {
            title: t('Contract Name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, row) => (
                <Space direction={'vertical'} size={0}>
                    <Typography.Text strong>{text}</Typography.Text>
                    <Typography.Text type={'secondary'}>{row.hospitalName}</Typography.Text>
                </Space>
            )
        },
        {
            title: t('Period'),
            key: 'period',
            width: 210,
            render: (text, row) => (
                <Space direction={'vertical'} size={0}>
                    <span>{dateFormat(row.startDate, 'DD.MM.YYYY')} – {dateFormat(row.endDate, 'DD.MM.YYYY')}</span>
                    {row.endDate < today && <Tag>{t('Ended')}</Tag>}
                    {row.startDate > today && <Tag color={'blue'}>{t('Not started')}</Tag>}
                </Space>
            )
        },
        {
            title: t('Modality / Device'),
            key: 'modalities',
            width: 180,
            render: (text, row) => (
                <Space size={[0, 4]} wrap>
                    {row.modalityIdList.map(id => <Tag key={id}>{modalityName(id)}</Tag>)}
                </Space>
            )
        },
        {
            title: t('Patient Classes'),
            key: 'studyTypes',
            width: 120,
            render: (text, row) => (
                <Space size={[0, 4]} wrap>
                    {PATIENT_CLASSES
                        .filter(x => row.studyTypeList.includes(x.value))
                        .map(x => (
                            <Tooltip key={x.code} title={t(x.label)}>
                                <Tag>{x.code}</Tag>
                            </Tooltip>
                        ))}
                </Space>
            )
        },
        {
            title: t('Usage'),
            key: 'usage',
            width: 260,
            render: (text, row) => {
                const color = CONTRACT_STATUS_COLOR[contractStatus(row)];
                return (
                    <Space direction={'vertical'} size={0} style={{ width: '100%' }}>
                        <Progress
                            percent={Math.min(row.usagePercent, 100)}
                            strokeColor={color}
                            format={() => <span style={{ color }}>%{row.usagePercent}</span>}
                        />
                        <Typography.Text type={'secondary'}>
                            {formatNumber(row.usedCount)} / {formatNumber(row.effectiveLimit)}
                            {row.increasePercent != null && ` (+%${row.increasePercent})`}
                        </Typography.Text>
                    </Space>
                )
            }
        },
        {
            title: t('Last Calculated'),
            key: 'calculatedAt',
            width: 170,
            render: (text, row) => (
                <Space>
                    <span>{row.calculatedAt ? dateFormat(row.calculatedAt) : '-'}</span>
                    <Tooltip title={t('Calculate now')}>
                        <Button
                            size={'small'}
                            type={'text'}
                            icon={<ReloadOutlined />}
                            loading={recalculatingId === row.id}
                            onClick={() => recalculate(row)}
                        />
                    </Tooltip>
                </Space>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 90,
            render: (text, row) => (
                <Space>
                    <Tooltip title={t('Edit')}>
                        <Button size={'small'} type={'text'} icon={<EditOutlined />} onClick={() => setEditing(row)} />
                    </Tooltip>
                    <Tooltip title={t('Delete')}>
                        <Button size={'small'} type={'text'} danger icon={<DeleteOutlined />} onClick={() => remove(row)} />
                    </Tooltip>
                </Space>
            )
        },
    ];

    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <Select
                    allowClear
                    showSearch
                    optionFilterProp={'label'}
                    placeholder={t('All hospitals')}
                    style={{ width: 280 }}
                    value={hospitalId}
                    onChange={(value) => setHospitalId(value ?? null)}
                    options={options.hospitals.map(x => ({ value: x.id, label: x.name }))}
                />
                <Button type={'primary'} icon={<PlusOutlined />} onClick={() => setEditing({})}>
                    {t('New Contract')}
                </Button>
            </Space>
            <Table
                rowKey={'id'}
                size={'small'}
                loading={loading}
                columns={columns}
                dataSource={contracts}
                pagination={false}
                scroll={{ x: 1000 }}
            />
            {editing && (
                <ContractEditModal
                    contract={editing.id ? editing : null}
                    options={options}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        // Hastane süreleri değişmiş olabilir; seçenekler de yenilenir.
                        await Promise.all([load(), loadOptions()])
                    }}
                    onIncreased={(row) => {
                        replaceRow(row)
                        setEditing(row)
                    }}
                />
            )}
        </>
    )
}
